import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import AppHeader from '../components/AppHeader';
import { FileText, Download, Loader2 } from 'lucide-react';

const fmtSize = (b) => {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};

export default function MyResourcesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/seeker/my-resources`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data || []);
    } catch (e) {
      toast.error('Could not load your resources.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const download = async (item) => {
    setDownloadingId(item.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/resources/${item.id}/download`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not open the file.');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF2F0]" data-testid="my-resources-page">
      <AppHeader title="Shared Resources" />
      <div className="container mx-auto max-w-2xl px-4 py-6">
        <p className="text-sm text-[#7A625C] mb-5">
          PDFs your care team has shared with you, across all your sessions. Only you can view these.
        </p>
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 size={28} className="animate-spin text-[#7D202B]" /></div>
        ) : items.length === 0 ? (
          <div className="py-16 text-center text-sm text-[#7A625C]" data-testid="my-resources-empty">
            No resources have been shared with you yet.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="my-resources-list">
            {items.map((it) => (
              <li key={it.id} className="flex items-center gap-3 p-4 rounded-2xl bg-white border border-[#FAC7C0]/60 shadow-sm" data-testid={`my-resource-${it.id}`}>
                <div className="w-11 h-11 rounded-xl bg-[#FAC7C0]/40 flex items-center justify-center shrink-0">
                  <FileText size={22} className="text-[#7D202B]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-[#3B2923] truncate" title={it.original_filename}>{it.original_filename}</div>
                  <div className="text-xs text-[#7A625C]">
                    {fmtSize(it.size)}
                    {it.session_grade ? ` · ${it.session_grade}` : ''}
                    {it.session_date ? ` · ${new Date(it.session_date).toLocaleDateString()}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => download(it)}
                  disabled={downloadingId === it.id}
                  data-testid={`my-resource-download-${it.id}`}
                  aria-label="View / download"
                  className="p-2 min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-[#7D202B] hover:bg-[#FDF2F0]"
                >
                  {downloadingId === it.id ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
